import {DateTime} from 'luxon';
import React, {FC, useRef, useState} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {useLayer} from '../../../../../libs/shared/ui/src/components/layers/layerContext';
import {useSaveAndRestoreFocusEffect} from '../../../../helpers/focusHelpers';
import {DatePickerContainer} from '../../../common/dateTime/datePickerContainer';
import {SideFrameDialogDefinition} from './sideFrameDialogsHooks';

/*
 * Props.
 */

interface SideFramePromptDateProps {
  datePromptData: PromptDateDefinition;
  onDateSelect: HandlerOf<DateTime | null>;
}

/*
 * Style.
 */

const StyledContentDiv = styled.div`
  padding: 6px 12px 12px 12px;
`;

/*
 * Component.
 */

const SideFramePromptDate: FC<SideFramePromptDateProps> = ({datePromptData, onDateSelect}) => {
  const {val, before, after, position} = datePromptData;
  const {left, top} = position || {top: 100, left: 100};

  // We need to approximate the different between screen coordinates and viewport coordinates.
  const {window} = useLayer();
  const {outerWidth, outerHeight, innerWidth, innerHeight} = window;
  const horizontalOffset = (outerWidth - innerWidth) / 2; // We assume the viewport is centered horizontally.
  const verticalOffset = outerHeight - innerHeight; // We assume the viewport is at the bottom.

  const saveAndRestoreFocusContextRef = useRef<HTMLElement>(null);
  useSaveAndRestoreFocusEffect(saveAndRestoreFocusContextRef);

  return (
    <>
      <Dropdown
        width={250}
        anchor={() => new DOMRect(left - horizontalOffset - 20, top - verticalOffset - 20, 40, 40)}
        placement="bottom"
        onRequestClose={() => onDateSelect(null)}
      >
        <StyledContentDiv>
          <DatePickerContainer
            highlightedDate={dateFromMillis(val)}
            minDate={dateFromMillis(before)}
            maxDate={dateFromMillis(after)}
            onDateSelect={onDateSelect}
          />
        </StyledContentDiv>
      </Dropdown>
      {/* We need a reference to an element outside the popover portal. */}
      <span ref={saveAndRestoreFocusContextRef} />
    </>
  );
};

function dateFromMillis(millis: number | undefined) {
  return millis !== undefined ? DateTime.fromMillis(millis) : undefined;
}

/*
 * Hook.
 */

interface Position {
  left: number;
  top: number;
}

interface PromptDateDefinition extends SideFrameDialogDefinition {
  position?: Position;
  val?: number;
  before?: number;
  after?: number;
}

export function useSideFramePromptDate() {
  const [promptDateData, setPromptDateData] = useState<PromptDateDefinition | null>(null);
  if (!promptDateData) {
    return {
      setPromptDateData,
      datePrompt: null,
    };
  }

  const onDateSelect = (date: DateTime | null) => {
    if (!promptDateData) {
      return;
    }

    promptDateData.onDone(date ? date.toMillis() : false);
    setPromptDateData(null);
  };

  return {
    setPromptDateData,
    promptDate: <SideFramePromptDate datePromptData={promptDateData} onDateSelect={onDateSelect} />,
  };
}
